/**
 * Global tooltip placement rule — the single positioning policy shared by
 * both renderers of the tooltip system (the React `TooltipContent` and the
 * DOM-level `nativeTooltipBridge`). One implementation guarantees every
 * tooltip in the suite obeys the same two invariants:
 *
 *  1. Containment — the bubble never leaves the window. After the side is
 *     chosen, both axes are clamped inside the viewport margins; the clamp
 *     ceiling is floored at the margin itself so a bubble larger than the
 *     viewport pins to the near edge instead of being pushed out the far one.
 *  2. No cursor cover — the pointer rests inside the anchor, so the bubble
 *     must never overlap the anchor rect. The preferred side wins when the
 *     window has room for it; when it does not (a top-bar trigger has
 *     nothing above it), the bubble flips to the opposite side. Only when
 *     NEITHER side fits does containment win and the anchor get overlapped.
 *
 * Pure geometry: no DOM access at module scope, so the DOM bridge and the
 * React primitive (and tests, if ever) share it without wiring.
 */

export type TooltipPlacementSide = 'top' | 'bottom' | 'left' | 'right';

/** The subset of DOMRect the placement math reads */
export interface TooltipAnchorRect {
    left: number;
    top: number;
    width: number;
    height: number;
    right: number;
    bottom: number;
}

export interface TooltipPlacementInput {
    /** The trigger's bounding rect, in viewport coordinates */
    anchor: TooltipAnchorRect;
    /** The rendered bubble's size */
    width: number;
    height: number;
    /** Preferred side — the central config default or a per-tooltip override */
    side: TooltipPlacementSide;
    /** Trigger→bubble gap in px */
    sideOffset: number;
    /** Minimum distance kept between the bubble and the window edges */
    margin?: number;
    /** Viewport the bubble must stay inside; defaults to the window */
    viewport?: { width: number; height: number };
}

export interface TooltipPlacement {
    left: number;
    top: number;
    /** The side actually used after the flip pass */
    side: TooltipPlacementSide;
}

export const TOOLTIP_VIEWPORT_MARGIN = 4;

const OPPOSITE_SIDE: Record<TooltipPlacementSide, TooltipPlacementSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
};

/** Distance between the anchor's facing edge and the window edge on `side` */
const roomOnSide = (
    side: TooltipPlacementSide,
    anchor: TooltipAnchorRect,
    viewport: { width: number; height: number },
): number => {
    switch (side) {
        case 'top':
            return anchor.top;
        case 'bottom':
            return viewport.height - anchor.bottom;
        case 'left':
            return anchor.left;
        case 'right':
            return viewport.width - anchor.right;
    }
};

/** Center on the anchor along the cross axis, gap it along the main axis —
    the bubble never overlaps the anchor rect on the side it is placed on */
const placeOnSide = (
    side: TooltipPlacementSide,
    anchor: TooltipAnchorRect,
    width: number,
    height: number,
    offset: number,
): { left: number; top: number } => {
    switch (side) {
        case 'top':
            return {
                left: anchor.left + anchor.width / 2 - width / 2,
                top: anchor.top - height - offset,
            };
        case 'bottom':
            return {
                left: anchor.left + anchor.width / 2 - width / 2,
                top: anchor.bottom + offset,
            };
        case 'left':
            return {
                left: anchor.left - width - offset,
                top: anchor.top + anchor.height / 2 - height / 2,
            };
        case 'right':
            return {
                left: anchor.right + offset,
                top: anchor.top + anchor.height / 2 - height / 2,
            };
    }
};

export function placeTooltipInViewport(
    input: TooltipPlacementInput,
): TooltipPlacement {
    const { anchor, width, height, side, sideOffset } = input;
    const margin = input.margin ?? TOOLTIP_VIEWPORT_MARGIN;
    const viewport = input.viewport ?? {
        width: window.innerWidth,
        height: window.innerHeight,
    };

    const fitsOn = (candidate: TooltipPlacementSide): boolean =>
        roomOnSide(candidate, anchor, viewport) - sideOffset >=
        (candidate === 'top' || candidate === 'bottom' ? height : width) + margin;

    // Preferred side when it fits; the opposite side when only it fits; the
    // preference stands when neither fits — containment clamps below.
    const effectiveSide =
        fitsOn(side) || !fitsOn(OPPOSITE_SIDE[side]) ? side : OPPOSITE_SIDE[side];

    let { left, top } = placeOnSide(effectiveSide, anchor, width, height, sideOffset);

    const maxX = Math.max(margin, viewport.width - width - margin);
    const maxY = Math.max(margin, viewport.height - height - margin);
    left = Math.min(Math.max(margin, left), maxX);
    top = Math.min(Math.max(margin, top), maxY);

    return { left, top, side: effectiveSide };
}
